import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const DEMO_PERSONAS = [
  { key: "executive", label: "Executive", email: "elena.torres@techstone.demo", desc: "Visión corporativa, decisiones, riesgos y prioridades" },
  { key: "pm", label: "Project Manager", email: "laura.martin@techstone.demo", desc: "Proyecto, planning, economía, riesgos y reuniones" },
  { key: "legal", label: "Legal / Corporate", email: "carlos.vega@techstone.demo", desc: "Contratos, obligaciones, documentos, pólizas y aprobaciones" },
  { key: "finance", label: "Finance", email: "javier.ruiz@techstone.demo", desc: "Compras, certificaciones, facturas, pagos y desviaciones" },
  { key: "external", label: "External / Contractor", email: "obra@tecnobuild-demo.es", desc: "Acceso estrictamente limitado a información asignada" },
];

const DEMO_USERS = [
  { email: "cayo.cesar.huerta@gmail.com", name: "Cayo César Huerta", role: "Demo Admin" },
  { email: "elena.torres@techstone.demo", name: "Elena Torres", role: "Managing Director" },
  { email: "carlos.vega@techstone.demo", name: "Carlos Vega", role: "Legal Director" },
  { email: "laura.martin@techstone.demo", name: "Laura Martín", role: "Project Director" },
  { email: "javier.ruiz@techstone.demo", name: "Javier Ruiz", role: "Finance Manager" },
  { email: "marta.soler@techstone.demo", name: "Marta Soler", role: "Asset Manager" },
  { email: "daniel.ortega@techstone.demo", name: "Daniel Ortega", role: "BD Manager" },
  { email: "isabel.ferrer@techstone.demo", name: "Isabel Ferrer", role: "Reviewer" },
  { email: "pablo.medina@techstone.demo", name: "Pablo Medina", role: "Contributor" },
  { email: "adrian.sanz@techstone.demo", name: "Adrián Sanz", role: "Viewer (solo lectura)" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.name}`);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "No se pudo iniciar sesión"));
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = (u) => {
    setEmail(u.email);
    setPassword("Demo123");
  };

  const personaLogin = async (p) => {
    setLoading(true);
    setError("");
    try {
      const user = await login(p.email, "Demo123");
      toast.success(`Bienvenido, ${user.name}`);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "No se pudo iniciar sesión"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="login-page">
      <div className="ts-sidebar hidden w-1/2 flex-col justify-between p-12 lg:flex">
        <div className="flex items-center gap-3">
          <div className="ts-brand-sig-inverse">TS</div>
          <div>
            <p className="font-display text-lg font-bold tracking-wide text-white">TECH STONE</p>
            <p className="text-xs uppercase tracking-widest text-[#8A9CC9]">Workspace · Entorno de demostración</p>
          </div>
        </div>
        <div>
          <h1 className="font-display text-4xl font-bold leading-tight text-white">
            Cuadro de mando,<br />procesos controlados<br />y trazabilidad completa.
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400">
            Entorno de demostración con datos sintéticos. Ningún estado se cambia libremente:
            cada transición ejecuta reglas validadas en el servidor.
          </p>
        </div>
        <p className="text-xs text-slate-600">Seed: TECH_STONE_FOUNDATION_DEMO_V1 — todos los datos son ficticios.</p>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <p className="font-display text-xl font-bold text-slate-900">Tech Stone Workspace</p>
            <p className="text-xs uppercase tracking-widest text-slate-500">Fundación funcional · Demo</p>
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Iniciar sesión</h2>
          <p className="mt-1 text-sm text-slate-500">Selecciona un usuario demo o introduce tus credenciales.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="usuario@techstone.demo" required data-testid="login-email-input" autoComplete="username" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Demo123" required data-testid="login-password-input" autoComplete="current-password" />
            </div>
            {error && (
              <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="login-error">{error}</div>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              <KeyRound className="mr-2 h-4 w-4" /> {loading ? "Accediendo…" : "Entrar"}
            </Button>
          </form>

          <div className="mt-8">
            <p className="ts-caption mb-2">Personas de demostración — entra con un clic (login real, permisos reales)</p>
            <div className="space-y-1.5" data-testid="persona-selector">
              {DEMO_PERSONAS.map((p) => (
                <button key={p.key} type="button" onClick={() => personaLogin(p)} disabled={loading} data-testid={`persona-${p.key}`}
                  className="flex w-full items-center justify-between rounded-sm border border-slate-200 bg-white px-3 py-2 text-left transition-colors hover:border-blue-400 hover:bg-blue-50">
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{p.label}</p>
                    <p className="text-[10px] text-slate-500">{p.desc}</p>
                  </div>
                  <KeyRound className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <p className="ts-caption mb-2">Usuarios demo (contraseña: Demo123)</p>
            <div className="grid grid-cols-2 gap-1.5">
              {DEMO_USERS.map((u) => (
                <button
                  key={u.email}
                  type="button"
                  onClick={() => quickLogin(u)}
                  data-testid={`quick-login-${u.email}`}
                  className={`rounded-sm border px-2.5 py-2 text-left transition-colors ${email === u.email ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"}`}
                >
                  <p className="truncate text-xs font-medium text-slate-800">{u.name}</p>
                  <p className="truncate text-[10px] text-slate-500">{u.role}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
